import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Search } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { formatDate } from '../../../../utils/dateUtils';
import { MarketReview } from '../../../../types/market-review';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AutoExpandingTextarea from '../../../../components/common/AutoExpandingTextarea';

interface LotOption {
  lot_id: string;
  community_name: string | null;
}

interface ReviewTypeOption {
  id: number;
  review_type: string;
  description: string;
}

interface EditMarketReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  review: MarketReview;
}

export default function EditMarketReviewModal({ isOpen, onClose, onSuccess, review }: EditMarketReviewModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const MAX_COMMENT_LENGTH = 200;
  const [formData, setFormData] = useState({
    lot: review.review_lot || '',
    date: review.review_date || '',
    type: review.review_type || '',
    ucv: review.review_ucv?.toString() || '',
    nextReview: review.review_next || '',
    comment: review.review_comment || ''
  });

  // Fetch lots data
  const { 
    data: lots = [], 
    isLoading: isLoadingLots, 
    error: lotsError 
  } = useQuery<LotOption[]>({
    queryKey: ['lots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('land_lots')
        .select('lot_id, lot_community')
        .order('lot_id')
        .limit(500); // Reasonable limit to avoid performance issues
        
      if (error) {
        console.error('Error fetching lots:', error);
        throw error;
      }
      
      // Transform the data to match the LotOption interface
      return (data || []).map((lot: any) => ({
        lot_id: lot.lot_id,
        community_name: lot.lot_community
      }));
    },
    staleTime: 5 * 60 * 1000, // Keep data fresh for 5 minutes
    enabled: isOpen // Only fetch when modal is open
  });

  // Fetch review types data
  const { 
    data: reviewTypes, 
    isLoading: isLoadingReviewTypes, 
    error: reviewTypesError 
  } = useQuery<ReviewTypeOption[]>({
    queryKey: ['review_types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_review_types')
        .select('id, review_type, description')
        .order('id');
        
      if (error) {
        console.error('Error fetching review types:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: isOpen // Only fetch when modal is open
  });

  // Reset form and errors when modal opens/closes or review changes
  useEffect(() => {
    if (isOpen && review) {
      setFormData({
        lot: review.review_lot || '',
        date: review.review_date || '',
        type: review.review_type || '',
        ucv: review.review_ucv?.toString() || '',
        nextReview: review.review_next || '',
        comment: review.review_comment || ''
      });
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, review]);

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.lot) {
      errors.lot = 'Lot is required';
    }
    
    if (formData.date && formData.nextReview) {
      const reviewDate = new Date(formData.date);
      const nextReviewDate = new Date(formData.nextReview);
      
      // Check if next review date is after review date
      if (reviewDate > nextReviewDate) {
        errors.nextReview = 'Next review date must be after review date';
      } else {
        // Calculate difference in days
        const diffTime = nextReviewDate.getTime() - reviewDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 30) {
          errors.nextReview = 'Next review date must be at least 30 days after review date';
        }
      }
    }
    
    if (formData.comment && formData.comment.length > MAX_COMMENT_LENGTH) {
      errors.comment = `Notes and Docs cannot exceed ${MAX_COMMENT_LENGTH} characters`;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Check if we're changing the lot or date and if the new combination already exists
      // (excluding the current review)
      if ((formData.lot !== review.review_lot || formData.date !== review.review_date) && 
          formData.lot && formData.date) {
        const { data: existingReview, error: checkError } = await supabase
          .from('market_reviews')
          .select('review_lot, review_date')
          .eq('review_lot', formData.lot)
          .eq('review_date', formData.date)
          .not('review_lot', 'eq', review.review_lot) // Exclude the current review
          .not('review_date', 'eq', review.review_date)
          .maybeSingle();
          
        if (checkError) {
          console.error('Error checking for existing market review:', checkError);
          throw checkError;
        }
        
        if (existingReview) {
          throw new Error(`A market review for lot ${formData.lot} on ${formData.date} already exists.`);
        }
      }
      
      // Update the market review record
      const { error: updateError } = await supabase
        .from('market_reviews')
        .update({
          review_lot: formData.lot,
          review_date: formData.date || null,
          review_type: formData.type || null,
          review_ucv: formData.ucv ? parseFloat(formData.ucv) : null,
          review_next: formData.nextReview || null,
          review_comment: formData.comment || null
        })
        .match({
          review_lot: review.review_lot,
          review_date: review.review_date
        });

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating market review record:', err);
      setError(err instanceof Error ? err.message : 'Failed to update market review record');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium">Edit Market Review</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lot *
            </label>
            <div className="relative mt-1">
              {isLoadingLots ? (
                <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50">
                  Loading lots...
                </div>
              ) : lotsError ? (
                <div className="mt-1 block w-full rounded-md border border-red-300 px-3 py-2 bg-red-50 text-red-700">
                  Error loading lots
                </div>
              ) : (
                <select
                  value={formData.lot}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, lot: e.target.value }));
                    if (validationErrors.lot) {
                      setValidationErrors(prev => ({ ...prev, lot: '' }));
                    }
                  }}
                  className={`block w-full rounded-md border ${
                    validationErrors.lot ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2 focus:border-primary focus:ring-primary`}
                  required
                >
                  <option value="">Select a lot</option>
                  {lots.map(lot => (
                    <option key={lot.lot_id} value={lot.lot_id}>
                      {lot.lot_id} {lot.community_name ? `(${lot.community_name})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {lots.length === 500 && (
                <p className="mt-1 text-xs text-amber-500">
                  Showing maximum of 500 lots.
                </p>
              )}
            </div>
            {validationErrors.lot && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.lot}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">Lot identifier</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Review Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">Date of the market review</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Review Type
            </label>
            <div className="relative mt-1">
              {isLoadingReviewTypes ? (
                <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50">
                  Loading review types...
                </div>
              ) : reviewTypesError ? (
                <div className="mt-1 block w-full rounded-md border border-red-300 px-3 py-2 bg-red-50 text-red-700">
                  Error loading review types
                </div>
              ) : (
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">Select a review type</option>
                  {reviewTypes?.map(type => (
                    <option key={type.id} value={type.review_type}>
                      {type.description} ({type.review_type})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Type of market review</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              UCV (Unimproved Capital Value)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.ucv}
              onChange={(e) => setFormData(prev => ({ ...prev, ucv: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              placeholder="Enter UCV amount"
            />
            <p className="mt-1 text-xs text-gray-500">Unimproved capital value in dollars</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Next Review Date
            </label>
            <input
              type="date"
              value={formData.nextReview}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, nextReview: e.target.value }));
                if (validationErrors.nextReview) {
                  setValidationErrors(prev => ({ ...prev, nextReview: '' }));
                }
              }}
              className={`mt-1 block w-full rounded-md border ${
                validationErrors.nextReview ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary focus:ring-primary`}
              min={formData.date ? (() => {
                const minDate = new Date(formData.date);
                minDate.setDate(minDate.getDate() + 30);
                return minDate.toISOString().split('T')[0];
              })() : undefined}
            />
            {validationErrors.nextReview && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.nextReview}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">Must be at least 30 days after the review date</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes and Docs
            </label>
            <AutoExpandingTextarea
              value={formData.comment}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, comment: e.target.value }));
                if (validationErrors.comment) {
                  setValidationErrors(prev => ({ ...prev, comment: '' }));
                }
              }}
              maxLength={255} // Database limit
              minHeight={80}
              maxHeight={200}
              className={`mt-1 block w-full rounded-md border ${
                validationErrors.comment ? 'border-red-500' : 
                formData.comment.length > MAX_COMMENT_LENGTH ? 'border-red-300' :
                formData.comment.length > MAX_COMMENT_LENGTH * 0.8 ? 'border-amber-300' : 'border-gray-300'
              } px-3 py-2 focus:border-primary focus:ring-primary`}
              placeholder="Enter notes about the Market Review"
            />
            {validationErrors.comment && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.comment}
              </p>
            )}
            <div className="flex flex-col mt-1 space-y-1">
              <p className="text-xs text-gray-500">
                Enter notes about the Market Review (max 200 characters)<br />
                Add URL to the document repository as needed
              </p>
              <div className="flex justify-end">
                <p className={`text-xs ${
                  formData.comment.length > MAX_COMMENT_LENGTH ? 'text-red-500 font-medium' :
                  formData.comment.length > MAX_COMMENT_LENGTH * 0.8 ? 'text-amber-500 font-medium' : 'text-gray-500'
                }`}>
                  {formData.comment.length}/{MAX_COMMENT_LENGTH}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
